import { Box, Typography } from "@mui/material";
import React from "react";
import { ReservationMonthStat } from "../dto/dashboard.dto";
import { niceMax } from "../utils/format.util";

export interface MonthlyReservationsChartProps {
  data: ReservationMonthStat[];
}

const CHART_HEIGHT = 180;
const BAR_MAX_WIDTH = 24;

export const MonthlyReservationsChartComponent: React.FC<
  MonthlyReservationsChartProps
> = ({ data }) => {
  const maxValue = niceMax(Math.max(...data.map((d) => d.count), 1));
  const peakIndex = data.reduce(
    (bestIdx, d, idx, arr) => (d.count > arr[bestIdx].count ? idx : bestIdx),
    0
  );
  const lastIndex = data.length - 1;

  return (
    <Box
      sx={{
        backgroundColor: "#FFFDF7",
        borderRadius: "14px",
        padding: "18px 20px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        flex: "1 1 380px",
        minWidth: 0,
      }}
    >
      <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#333", mb: 2 }}>
        Réservations (6 derniers mois)
      </Typography>

      <Box sx={{ display: "flex" }}>
        {/* Y-axis ticks */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            height: CHART_HEIGHT,
            pr: 1,
            pb: "24px",
          }}
        >
          {[maxValue, maxValue / 2, 0].map((tick) => (
            <Typography
              key={tick}
              sx={{ fontSize: "10px", color: "#898781", lineHeight: 1 }}
            >
              {Math.round(tick)}
            </Typography>
          ))}
        </Box>

        {/* Chart area */}
        <Box
          sx={{
            position: "relative",
            flex: 1,
            height: CHART_HEIGHT,
            display: "flex",
            alignItems: "flex-end",
            gap: "2px",
          }}
        >
          {/* Gridlines */}
          {[0, 0.5, 1].map((fraction) => (
            <Box
              key={fraction}
              sx={{
                position: "absolute",
                left: 0,
                right: 0,
                bottom: `${fraction * 100}%`,
                borderTop: "1px solid #e1e0d9",
              }}
            />
          ))}

          {data.map((d, idx) => {
            const heightPct = Math.max((d.count / maxValue) * 100, d.count > 0 ? 3 : 0);
            const showDirectLabel = idx === peakIndex || idx === lastIndex;
            return (
              <Box
                key={d.month}
                tabIndex={0}
                sx={{
                  flex: 1,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  position: "relative",
                  outline: "none",
                  "&:hover .bar-tooltip, &:focus-visible .bar-tooltip": {
                    opacity: 1,
                  },
                  "&:hover .bar-fill, &:focus-visible .bar-fill": {
                    filter: "brightness(1.1)",
                  },
                }}
              >
                {/* Tooltip */}
                <Box
                  className="bar-tooltip"
                  sx={{
                    position: "absolute",
                    bottom: `calc(${heightPct}% + 8px)`,
                    opacity: 0,
                    transition: "opacity 0.15s ease",
                    backgroundColor: "#2d2d2d",
                    color: "#fff",
                    fontSize: "11px",
                    fontWeight: 600,
                    borderRadius: "6px",
                    padding: "3px 8px",
                    whiteSpace: "nowrap",
                    pointerEvents: "none",
                    zIndex: 1,
                  }}
                >
                  {d.label} : {d.count}
                </Box>

                {/* Direct label for peak/last */}
                {showDirectLabel && (
                  <Typography
                    className="bar-tooltip"
                    sx={{
                      position: "absolute",
                      bottom: `calc(${heightPct}% + 4px)`,
                      fontSize: "10px",
                      fontWeight: 700,
                      color: "#B4006E",
                      opacity: 1,
                    }}
                  >
                    {d.count}
                  </Typography>
                )}

                <Box
                  className="bar-fill"
                  sx={{
                    width: "100%",
                    maxWidth: BAR_MAX_WIDTH,
                    height: `${heightPct}%`,
                    background: "linear-gradient(180deg, #FF008D, #B4006E)",
                    borderRadius: "4px 4px 0 0",
                    transition: "filter 0.15s ease",
                  }}
                />
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* X-axis labels */}
      <Box sx={{ display: "flex", pl: "28px", gap: "2px", mt: 0.5 }}>
        {data.map((d) => (
          <Typography
            key={d.month}
            sx={{
              flex: 1,
              textAlign: "center",
              fontSize: "11px",
              color: "#898781",
            }}
          >
            {d.label}
          </Typography>
        ))}
      </Box>
    </Box>
  );
};
